import { DEFAULT_LOCALE, isAppLocale, type AppLocale } from '../i18n'
import type { components } from './generated/schema'
import {
  asCount,
  asNullableText,
  asNumber,
  asStringList,
  asText,
  FormatError,
  isRecord,
} from './parse'

type Schemas = components['schemas']

function requireRecord(value: unknown): Record<string, unknown> {
  if (!isRecord(value)) throw new FormatError('Malformed profile response')
  return value
}

function localeOrDefault(value: unknown): AppLocale {
  const candidate = typeof value === 'string' ? value : null
  return isAppLocale(candidate) ? candidate : DEFAULT_LOCALE
}

function optionalLocale(value: unknown): AppLocale | null {
  const candidate = typeof value === 'string' ? value : null
  return isAppLocale(candidate) ? candidate : null
}

/**
 * Normalises the private `/me` projection from legacy rows where scalars and
 * lists do not always match the OpenAPI types. Valid fields are preserved;
 * non-string text becomes '', malformed string lists become [], numeric
 * strings parse for ratings/counters, and optional values keep their null
 * defaults. The exported types do not change and screens keep receiving the
 * same projection.
 */
export function normalizePrivateProfile(
  value: unknown,
): Schemas['PrivateProfile'] {
  const record = requireRecord(value)
  return {
    id: asText(record.id),
    email: asText(record.email),
    age: asNumber(record.age),
    isAdmin: record.isAdmin === true,
    nickname: asText(record.nickname),
    publicName: asText(record.publicName),
    firstName: asText(record.firstName),
    lastName: asText(record.lastName),
    avatarUrl: asNullableText(record.avatarUrl),
    city: asNullableText(record.city),
    bio: asNullableText(record.bio),
    languages: asStringList(record.languages),
    interests: asStringList(record.interests),
    rating: asNumber(record.rating),
    ratingCount: asCount(record.ratingCount),
    activitiesCreated: asCount(record.activitiesCreated),
    activitiesJoined: asCount(record.activitiesJoined),
    activitiesParticipated: asCount(record.activitiesParticipated),
    profileCompleteForParticipation:
      record.profileCompleteForParticipation === true,
    locale: localeOrDefault(record.locale),
  }
}

/**
 * `PATCH /me` and the avatar upload return `ProfileSubset`. `locale` is
 * optional in the frozen schema, so a missing/invalid value is omitted instead
 * of overwriting the locale already held in the profile screen's state.
 */
export function normalizeProfileSubset(
  value: unknown,
): Schemas['ProfileSubset'] {
  const record = requireRecord(value)
  const subset: Schemas['ProfileSubset'] = {
    id: asText(record.id),
    nickname: asText(record.nickname),
    publicName: asText(record.publicName),
    firstName: asText(record.firstName),
    lastName: asText(record.lastName),
    avatarUrl: asNullableText(record.avatarUrl),
    city: asNullableText(record.city),
    bio: asNullableText(record.bio),
    languages: asStringList(record.languages),
    interests: asStringList(record.interests),
  }
  const locale = optionalLocale(record.locale)
  if (locale) subset.locale = locale
  return subset
}

/** Public profiles expose the same legacy scalar/list inconsistencies. */
export function normalizePublicProfile(
  value: unknown,
): Schemas['PublicProfile'] {
  const record = requireRecord(value)
  return {
    id: asText(record.id),
    nickname: asText(record.nickname),
    age: asNumber(record.age),
    publicName: asText(record.publicName),
    avatarUrl: asNullableText(record.avatarUrl),
    city: asNullableText(record.city),
    bio: asNullableText(record.bio),
    languages: asStringList(record.languages),
    interests: asStringList(record.interests),
    rating: asNumber(record.rating),
    ratingCount: asCount(record.ratingCount),
    activitiesCreated: asCount(record.activitiesCreated),
    activitiesJoined: asCount(record.activitiesJoined),
    activitiesParticipated: asCount(record.activitiesParticipated),
  }
}
